/*
 * Settings, Plan and billing (draft 11): what this location pays The Trades. A franchise sees its fee, its paid add-ons
 * with this month's use, its bills, and saves the card for the fee with Stripe's card fields on our own page. Head office
 * sets the fee and turns add-ons on; a late bill never locks the location's work.
 */
import { useRef, useState } from "react";
import { loadStripe, type Stripe, type StripeElements } from "@stripe/stripe-js";
import { useCardConfirm, useCardSetup, usePlan } from "../../api/hq";
import { useSession } from "../../app/session";
import { money, shortDate } from "../../components/format";
import { useToast } from "../../components/overlays";
import { Banner, Button, Card, Details, LoadError, Skeleton } from "../../components/parts";
import { AddonLine, BillTable } from "../HeadOffice";

export function PlanSettings() {
  const { company, can } = useSession();
  const q = usePlan(company.id, true);
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  const p = q.data;
  if (!p) return <Skeleton rows={4} />;
  if (p.status === "none")
    return (
      <Card title="Plan and billing">
        <p>Head office runs this location, so it pays The Trades no fee.</p>
      </Card>
    );
  const monthly = p.feeCents + p.addons.filter((a) => a.on).reduce((s, a) => s + a.priceCents, 0);
  const failed = p.bills.find((b) => b.status === "failed");
  return (
    <>
      {p.status === "ended" ? <Banner type="notice">This franchise has ended. No more bills come.</Banner> : null}
      {failed ? (
        <Banner type="error">
          The payment was turned down for {failed.month}. Your work goes on as usual. Add a new card or bank account to pay it.
        </Banner>
      ) : null}
      {p.addonsPaused ? (
        <Banner type="warning">
          Paid add-ons are paused because a bill is 30 days late. Everything else works. They start again once it is paid.
        </Banner>
      ) : null}
      <Card title="Plan and billing">
        <Details
          rows={[
            ["Software fee", `${money(p.feeCents)} a month`],
            ["Each month with add-ons", money(monthly)],
            ["Started", p.startedOn ? shortDate(p.startedOn) : "Not set"],
            ["Head office sees customer names", p.hqSeesNames ? "Yes" : "No, totals only"],
          ]}
        />
        <p className="muted small">Head office sets the fee and the add-ons. Ask head office to change them.</p>
      </Card>
      <Card title="Paid add-ons">
        {p.addons.map((a) => (
          <AddonLine key={a.addon} a={a} />
        ))}
      </Card>
      <Card title="How you pay the fee">
        {p.card ? (
          <p>
            {p.card.bank ? "Bank account" : p.card.brand} ending in {p.card.last4}. It is charged on the 1st for the month before.
            {p.card.bank ? " A bank payment takes a few days to clear." : ""}
          </p>
        ) : (
          <Banner type="warning">No card or bank account yet. Add one so the monthly fee can be paid.</Banner>
        )}
        {can("franchise.update") && p.status === "active" ? <CardSaver hasCard={Boolean(p.card)} /> : null}
      </Card>
      <Card title="Bills">
        <BillTable plan={p} />
      </Card>
    </>
  );
}

function CardSaver({ hasCard }: { hasCard: boolean }) {
  const start = useCardSetup();
  const confirm = useCardConfirm();
  const toast = useToast();
  const box = useRef<HTMLDivElement>(null);
  const [setup, setSetup] = useState<{ setupId: string; fake: boolean } | null>(null);
  const [stripe, setStripe] = useState<{ s: Stripe; e: StripeElements } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const begin = async () => {
    setErr(null);
    setBusy(true);
    try {
      const made = await start.mutateAsync();
      setSetup(made);
      if (!made.fake) {
        const st = await loadStripe(made.publishableKey);
        if (!st) throw new Error("Card fields could not load.");
        const el = st.elements({ clientSecret: made.clientSecret });
        setStripe({ s: st, e: el });
        requestAnimationFrame(() => {
          if (box.current) el.create("payment").mount(box.current);
        });
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!setup) return;
    setErr(null);
    setBusy(true);
    try {
      if (stripe) {
        const r = await stripe.s.confirmSetup({
          elements: stripe.e,
          confirmParams: { return_url: window.location.href },
          redirect: "if_required",
        });
        if (r.error) throw new Error(r.error.message ?? "The card was not saved.");
      }
      await confirm.mutateAsync(setup.setupId);
      toast("Saved");
      setSetup(null);
      setStripe(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      {setup && !setup.fake ? <div ref={box} className="public-card" /> : null}
      {setup?.fake ? <Banner type="notice">Test mode here. Save uses the test card 4242.</Banner> : null}
      {err ? <Banner type="error">{err}</Banner> : null}
      <div className="row">
        <Button type={setup ? "primary" : "secondary"} loading={busy} onClick={() => void (setup ? save() : begin())}>
          {setup ? "Save" : hasCard ? "Change how you pay" : "Add a card or bank account"}
        </Button>
      </div>
    </div>
  );
}
